import { captureException } from '@sentry/browser';
import { fromByteArray } from 'base64-js';
import { hostedFields } from 'braintree-web';
import dayjs, { extend } from 'dayjs';
import dayjsUtcPlugin from 'dayjs/plugin/utc';
import { useTranslation } from '../hooks/useTranslations.js';
import { sanitizeCardType } from '../services/braintreeService.js';
import { BraintreeAuthenticationIndicator, BraintreeShippingNameIndicator, BraintreeAddressMatch } from '../types-internal/index.js';
import { gatherDataByFieldName, maybeTrackErrors, maybeBraintreeTracking, makeTrafficNotifications } from './braintreeFunctions.js';

extend(dayjsUtcPlugin);
/**
 * Get chunks of text
 * If you want to get first X letters: chunks(test, X)
 * If you want to skip first Y letters, then keep X letters: chunks(test, X, Y)
 * @param text text to slice
 * @param chunkSize length of text to keep
 * @param chunkIndex length of text to skip
 * @return slice of text
 */
const chunks = (text, chunkSize = 50, chunkIndex = 0) => {
    return text
        ? text.slice(chunkIndex * chunkSize, (chunkIndex + 1) * chunkSize) || undefined
        : undefined;
};
/**
 * Get only digits from text (ignore any other character)
 * @param text text to format
 * @return digits from text
 */
const digitsOnly = (text) => {
    return text ? text.replace(/[^\d]/g, '') : undefined;
};
const convertDate = (timestamp) => {
    if (!timestamp)
        return undefined;
    const date = dayjs(timestamp).format('YYYYMMDD');
    return date === 'Invalid Date' ? undefined : date;
};
function convertAddress(type, address) {
    if (!address)
        return undefined;
    const commonFields = {
        streetAddress: chunks(address.street, 50, 0),
        extendedAddress: chunks(address.street, 50, 1),
        line3: chunks(address.street, 50, 2), // Line 3 of the shipping address if needed (eg. suite, apt #, etc). (maximum length 50)
    };
    return type === 'shipping'
        ? {
            streetAddress: commonFields.streetAddress || '',
            extendedAddress: commonFields.extendedAddress || '',
            line3: commonFields.line3 || '',
            locality: chunks(address.city) || '',
            region: address.country,
            postalCode: chunks(address.postalCode, 10) || '',
            countryCodeAlpha2: chunks(address.isoCountry, 2) || '', // The 2 character country code.
        }
        : {
            streetAddress: commonFields.streetAddress,
            extendedAddress: commonFields.extendedAddress,
            line3: commonFields.line3,
            locality: address.city,
            region: address.country,
            postalCode: address.postalCode,
            countryCodeAlpha2: address.isoCountry,
            givenName: chunks(address.firstName),
            surname: chunks(address.lastName),
            phoneNumber: digitsOnly(address.phoneNumber), // The phone number associated with the billing address. Only numbers; remove dashes, parenthesis and other characters.
        };
}
const compareCardShippingNames = (shippingAddress, creditCardHolderName) => {
    if (!creditCardHolderName)
        return undefined;
    const shippingName = shippingAddress.firstName + ' ' + shippingAddress.lastName;
    return creditCardHolderName === shippingName
        ? BraintreeShippingNameIndicator.ACCOUNT_AND_SHIPPING_NAME_IDENTICAL_01
        : BraintreeShippingNameIndicator.ACCOUNT_AND_SHIPPING_NAME_DIFFERENT_02;
};
const compareBillingShippingAddress = (billingAddress, shippingAddress) => {
    if (!billingAddress || !shippingAddress)
        return undefined;
    if (billingAddress.firstName === shippingAddress.firstName &&
        billingAddress.lastName === shippingAddress.lastName &&
        billingAddress.street === shippingAddress.street &&
        billingAddress.company === shippingAddress.company &&
        billingAddress.city === shippingAddress.city &&
        billingAddress.isoCountry === shippingAddress.isoCountry &&
        billingAddress.country === shippingAddress.country &&
        billingAddress.postalCode === shippingAddress.postalCode &&
        billingAddress.phoneNumber === shippingAddress.phoneNumber)
        return BraintreeAddressMatch.BILLING_AND_SHIPPING_ADDRESS_IDENTICAL_01;
    else
        return BraintreeAddressMatch.BILLING_AND_SHIPPING_ADDRESS_DIFFERENT_02;
};
/**
 * Format additional information in correct Braintree format
 * See Braintree docs: https://braintree.github.io/braintree-web/current/ThreeDSecure.html#~additionalInformation
 * @param isSubscriptionFirst true if payment is the first of a subscription
 * @param subscriptionFirstInterval number of days of the subscription
 * @param raw object with info from checkout to format
 * @return formatted additional information
 */
const formatAdditionalInformation = (isSubscriptionFirst, subscriptionFirstInterval, raw) => {
    const minimalAdditionalInformation = {
        authenticationIndicator: isSubscriptionFirst // The 2-digit number indicating the type of authentication request
            ? BraintreeAuthenticationIndicator.RECURRING_TRANSACTION_02
            : BraintreeAuthenticationIndicator.PAYMENT_01,
        recurringFrequency: isSubscriptionFirst
            ? chunks(digitsOnly(subscriptionFirstInterval), 3)
            : undefined,
        recurringEnd: isSubscriptionFirst ? dayjs.utc().add(100, 'year').format('YYYYMMDD') : undefined,
        purchaseDate: dayjs.utc().format('YYYYMMDDHHmmss'),
        productCode: 'GEN', // The 3-letter string representing the merchant product code. Possible values: GEN for General Retail.
    };
    if (!raw)
        return minimalAdditionalInformation;
    return {
        ...minimalAdditionalInformation,
        shippingAddress: convertAddress('shipping', raw.shippingAddress),
        shippingGivenName: chunks(raw.shippingAddress.firstName),
        shippingSurname: chunks(raw.shippingAddress.lastName),
        shippingPhone: chunks(digitsOnly(raw.shippingAddress.phoneNumber), 20),
        accountCreateDate: convertDate(raw.accountCreateDate),
        shippingNameIndicator: compareCardShippingNames(raw.shippingAddress, raw.creditCardHolderName),
        paymentAccountAge: convertDate(raw.creditCardCreateDate),
        addressMatch: compareBillingShippingAddress(raw.billingAddress, raw.shippingAddress),
        accountId: chunks(raw.accountId.toString(), 64),
        ipAddress: raw.ipAddress,
        orderDescription: chunks(raw.articleIds.join(','), 256),
        userAgent: chunks(raw.userAgent, 500), // The exact content of the HTTP user agent header. (maximum length 500)
    };
};
const formatCustomFields = (cardType, isSubscriptionFirst, customerId, fraudPreventionRecommendation, force3dsChallengeEnabled) => {
    const customFields = {
        card_type: cardType,
        subscription_first: String(isSubscriptionFirst),
        ...(customerId && { customer_id: customerId }),
        fraud_prevention_recommendation: fraudPreventionRecommendation,
        force_3ds_challenge_enabled: force3dsChallengeEnabled,
    };
    return customFields;
};
const stringToBase64 = (data) => {
    const utf8TextEncoder = new TextEncoder();
    return fromByteArray(utf8TextEncoder.encode(data));
};
const stringUriToBase64 = (data) => {
    return stringToBase64(data).replaceAll('/', '_').replaceAll('+', '-').replaceAll('=', '');
};
const useBraintreeHostedFields = (braintreeInstanceRef) => {
    const { t } = useTranslation();
    return async () => (await hostedFields.create({
        client: braintreeInstanceRef.current,
        styles: {
            input: {
                'font-size': '15px',
                padding: '15px',
            },
            select: {
                padding: '14px',
                marginRight: '-45px',
                appearance: 'none',
            },
        },
        fields: {
            number: {
                selector: '#cc-number',
                placeholder: t('checkout.creditcard.form.number.placeholder'),
            },
            cardholderName: {
                selector: '#cardholder-name',
                placeholder: t('checkout.creditcard.form.cardholdername.placeholder'),
                maxlength: 15,
            },
            cvv: {
                selector: '#cc-cvv',
                placeholder: t('checkout.creditcard.form.securitycode.placeholder'),
            },
            expirationMonth: {
                selector: '#expiration-month',
                placeholder: t('checkout.creditcard.form.expirationmonth.placeholder'),
                select: {
                    options: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
                },
            },
            expirationYear: {
                selector: '#expiration-year',
                placeholder: t('checkout.creditcard.form.expirationyear.placeholder'),
                select: true,
            },
        },
    }));
};
const braintreeTokenizeCard = ({ setError, hostedFieldsInstance, isCurrentCardNumberSupported, updateValidity, updateEmpty, isLocalEnv, storeInVault, containerType, }) => {
    return async () => {
        setError(undefined);
        const state = hostedFieldsInstance.current?.getState();
        let result;
        if (state) {
            // First : check validity from braintree SDK
            const invalidKeys = Object.entries(state.fields)
                .filter((entry) => !entry[1].isValid)
                .map((entry) => entry[0]);
            const isSupported = isCurrentCardNumberSupported();
            // Then : check supported cc validity manually , since braintree does not do it
            if (Object.keys(invalidKeys).length || !isSupported) {
                const { validityByFieldName, emptinessByFieldName, trafficNotifications } = gatherDataByFieldName(state, isSupported);
                updateValidity(validityByFieldName);
                updateEmpty(emptinessByFieldName);
                maybeTrackErrors(containerType, trafficNotifications);
                result = {
                    success: false,
                    errorMessage: `Some fields are invalid : [${invalidKeys.join(', ')}]`,
                    errorDisplayable: false,
                };
            }
            else {
                try {
                    const tokenized = (await hostedFieldsInstance.current?.tokenize({
                        vault: false,
                    }));
                    if (isLocalEnv)
                        console.log('Tokenization result :', tokenized);
                    if (tokenized) {
                        maybeBraintreeTracking(containerType, 'BTformOK');
                        result = {
                            success: true,
                            data: {
                                ...tokenized,
                                details: {
                                    ...tokenized.details,
                                    cardType: sanitizeCardType(tokenized.details.cardType),
                                },
                                storeInVault,
                            },
                        };
                    }
                    else {
                        captureException({
                            message: 'Cannot tokenize hosted fields. Result is empty.',
                        });
                        result = {
                            success: false,
                            errorMessage: 'Cannot tokenize hosted fields. Result is empty.',
                            errorDisplayable: true,
                        };
                    }
                }
                catch (error) {
                    captureException({ error, message: 'Cannot tokenize hosted fields' });
                    maybeTrackErrors(containerType, makeTrafficNotifications(error));
                    result = {
                        success: false,
                        errorMessage: 'Cannot tokenize hosted fields',
                        errorDisplayable: true,
                    };
                }
            }
        }
        else {
            captureException({ message: 'State is not initialized yet' });
            result = {
                success: false,
                errorMessage: 'State is not initialized yet',
                errorDisplayable: true,
            };
        }
        return result;
    };
};
const getBraintreeStatusCallbacks = (intialized, fieldsValidity, hostedFieldsCards, supportedCardTypes, fieldsEmpty) => {
    const isInvalid = (name) => {
        // undefined means no validation yet so we need to test === false
        return intialized && fieldsValidity[name] === false;
    };
    const isInvalidEmpty = (name) => {
        // undefined means no validation yet so we need to test === true
        return intialized && fieldsEmpty[name] === true;
    };
    const getActiveCardConfig = (cardType) => {
        return hostedFieldsCards.find((card) => sanitizeCardType(card.type) === cardType);
    };
    const isCardTypeActive = (cardType) => {
        return !!getActiveCardConfig(cardType) && hostedFieldsCards.length >= supportedCardTypes.length;
    };
    return {
        isInvalid,
        isInvalidEmpty,
        getActiveCardConfig,
        isCardTypeActive,
    };
};

export { braintreeTokenizeCard, chunks, convertAddress, digitsOnly, formatAdditionalInformation, formatCustomFields, getBraintreeStatusCallbacks, stringToBase64, stringUriToBase64, useBraintreeHostedFields };
//# sourceMappingURL=braintreeUtils.js.map
